import { MlbStatsHelper } from 'classes/helpers/mlb-stats.helper';
import { Controller } from 'lib_ts/enums/routing/controllers';
import { HittersRoutes } from 'lib_ts/enums/routing/routes/hitters';
import { Nullable, WithID } from 'lib_ts/interfaces/common/i-generics';
import { IServerResponse } from 'lib_ts/interfaces/common/i-server-response';
import { IHitter, IHitterUpsert } from 'lib_ts/interfaces/i-hitter';
import { IHitterProfileHitsFilter } from 'lib_ts/interfaces/i-hitter-profile';
import {
  IHitterUsageSummary,
  IHitterUsageSummaryFilters,
} from 'lib_ts/interfaces/session-hits/i-hitter-session-summary';
import { ISHMetadata } from 'lib_ts/interfaces/session-hits/i-metadata';
import {
  IDetectionIssue,
  ISessionHit,
  IUpdateHitRequest,
  IUpdateHitsRequest,
} from 'lib_ts/interfaces/session-hits/i-session-hit';
import {
  ISHSummary,
  ISHSummaryRequest,
} from 'lib_ts/interfaces/session-hits/i-summary';
import { BaseRESTService } from 'services/_base-rest.service';

export class HittersService extends BaseRESTService {
  private static instance: HittersService;
  static getInstance(): HittersService {
    if (!HittersService.instance) {
      HittersService.instance = new HittersService();
    }

    return HittersService.instance;
  }

  private constructor() {
    super({
      controller: Controller.Hitters,
    });
  }

  async getSessionSummaries(payload: ISHSummaryRequest): Promise<ISHSummary[]> {
    return await this.post(
      {
        uri: HittersRoutes.GetSessionSummaries,
      },
      payload
    )
      .then((result: IServerResponse) => {
        if (!result.success || !result.data || result.error) {
          console.error(
            result.error ?? 'encountered an error in getSessionSummaries'
          );
          return [];
        }

        return result.data as ISHSummary[];
      })
      .catch((error) => {
        console.error(error ?? 'encountered an error in getSessionSummaries');
        return [];
      });
  }

  async deleteSessions(ids: string[]): Promise<boolean> {
    return await this.post(
      {
        uri: HittersRoutes.DeleteSessions,
      },
      ids
    ).then((result: IServerResponse) => {
      if (!result.success || result.error) {
        console.error(result.error ?? 'encountered an error in deleteSessions');
        return false;
      }

      return true;
    });
  }

  async getSessionHits(hitterSessionId: string): Promise<ISessionHit[]> {
    return await this.get({
      uri: `${HittersRoutes.GetSessionHits}/${hitterSessionId}`,
    })
      .then((result: IServerResponse) => {
        if (!result.success || !result.data || result.error) {
          console.error(
            result.error ?? 'encountered an error in getSessionHits'
          );
          return [];
        }

        return result.data as ISessionHit[];
      })
      .catch((error) => {
        console.error(error ?? 'encountered an error in getSessionHits');
        return [];
      });
  }

  // for updating a single hit, e.g. exclude from avg, toggling swing, overriding hit outcome
  async updateHit(
    hitID: string,
    data: IUpdateHitRequest
  ): Promise<ISessionHit> {
    return await this.put(
      {
        uri: `${HittersRoutes.UpdateHit}/${hitID}`,
      },
      data
    );
  }

  // for reassigning hitters to a new hitter
  async updateHits(payload: IUpdateHitsRequest): Promise<boolean> {
    return await this.put(
      {
        uri: HittersRoutes.UpdateHits,
      },
      payload
    )
      .then((result: IServerResponse) => {
        if (!result.success || result.error) {
          console.error(result.error ?? 'encountered an error in updateHits');
          return false;
        }

        return true;
      })
      .catch((error) => {
        console.error(error ?? 'encountered an error in updateHits');
        return false;
      });
  }

  async getHitter(id: string): Promise<IHitter | undefined> {
    return await this.get({
      uri: `${HittersRoutes.GetHitter}/${id}`,
    }).then((result: IServerResponse) => {
      if (!result.success || !result.data || result.error) {
        console.error(result.error ?? 'encountered an error in getHitters');
        return undefined;
      }

      const output = result.data as IHitter;
      MlbStatsHelper.mixinPlayerTeamDetails(output);

      return output;
    });
  }

  async getHitters(): Promise<IHitter[]> {
    return await this.get({
      uri: HittersRoutes.GetHitters,
    }).then((result: IServerResponse) => {
      if (!result.success || !result.data || result.error) {
        console.error(result.error ?? 'encountered an error in getHitters');
        return [];
      }

      const output = result.data as IHitter[];

      return output.map((hitter) =>
        MlbStatsHelper.mixinPlayerTeamDetails(hitter)
      );
    });
  }

  /** use post to allow deleting multiple at once */
  async deleteHitters(hitterIDs: string[]): Promise<boolean> {
    return await this.post(
      {
        uri: HittersRoutes.DeleteHitters,
      },
      hitterIDs
    ).then((result) => !!result && result.success);
  }

  async createHitter(data: IHitterUpsert): Promise<IHitter> {
    const response = await this.post(
      {
        uri: HittersRoutes.CreateHitter,
      },
      data
    );

    return MlbStatsHelper.mixinPlayerTeamDetails(response);
  }

  async updateHitter(data: WithID<IHitterUpsert>): Promise<IHitter> {
    const response = await this.put(
      {
        uri: HittersRoutes.UpdateHitter,
        params: {
          hitter_id: data._id,
        } as any,
      },
      data
    );

    return MlbStatsHelper.mixinPlayerTeamDetails(response);
  }

  async uploadHitterAvatar(
    hitterID: string,
    formData: FormData
  ): Promise<IHitter | undefined> {
    return await this.post(
      {
        uri: HittersRoutes.UploadHitterAvatar,
        headers: { 'Content-Type': 'multipart/form-data' },
        params: {
          hitter_id: hitterID,
        } as any,
      },
      formData
    )
      .then((result: IServerResponse) => {
        if (!result.success || !result.data || result.error) {
          console.warn(
            result.error ?? 'encountered an error in uploadHitterAvatar'
          );
          return undefined;
        }

        return result.data as IHitter;
      })
      .catch((error) => {
        console.error(error ?? 'encountered an error in uploadHitterAvatar');
        return undefined;
      });
  }

  async uploadCsv(formData: FormData): Promise<IHitter[] | undefined> {
    return await this.post(
      {
        uri: HittersRoutes.UploadCsv,
        headers: { 'Content-Type': 'multipart/form-data' },
      },
      formData
    )
      .then((result: IServerResponse) => {
        if (!result.success || !result.data || result.error) {
          console.warn(result.error ?? 'encountered an error in uploadCsv');
          return undefined;
        }

        return result.data as IHitter[];
      })
      .catch((error) => {
        console.error(error ?? 'encountered an error in uploadCsv');
        return undefined;
      });
  }

  async getHit(hitID: string): Promise<ISessionHit | undefined> {
    return await this.get({
      uri: `${HittersRoutes.GetHit}/${hitID}`,
    });
  }

  async getHitByFire(fireID: string): Promise<ISessionHit | undefined> {
    return await this.get({
      uri: `${HittersRoutes.GetHitByFire}/${fireID}`,
    });
  }

  async getProfileHits(
    filter: IHitterProfileHitsFilter
  ): Promise<ISessionHit[]> {
    return await this.post(
      {
        uri: `${HittersRoutes.GetProfileHits}`,
      },
      filter
    )
      .then((result) => {
        if (!result.success || !result.data || result.error) {
          console.warn(
            result.error ?? 'encountered an error in getProfileHits'
          );
          return [];
        }

        return result.data as ISessionHit[];
      })
      .catch((error) => {
        console.error(error ?? 'encountered an error in getProfileHits');
        return [];
      });
  }

  async upsertMetadata(model: ISHMetadata): Promise<ISHMetadata | undefined> {
    return await this.post(
      {
        uri: HittersRoutes.UpsertMetadata,
      },
      model
    )
      .then((result: IServerResponse) => {
        if (!result.success || !result.data || result.error) {
          console.warn(
            result.error ?? 'encountered an error in upsertMetadata'
          );
          return undefined;
        }

        return result.data as ISHMetadata;
      })
      .catch((error) => {
        console.error(error ?? 'encountered an error in upsertMetadata');
        return undefined;
      });
  }

  async upsertDetectionIssue(
    hitID: string,
    data: Partial<IDetectionIssue>
  ): Promise<ISessionHit | undefined> {
    return await this.post(
      {
        uri: `${HittersRoutes.UpsertDetectionIssue}/${hitID}`,
      },
      data
    )
      .then((result: IServerResponse) => {
        if (!result.success || !result.data || result.error) {
          console.warn(
            result.error ?? 'encountered an error in upsertDetectionIssue'
          );
          return undefined;
        }

        return result.data as ISessionHit;
      })
      .catch((error) => {
        console.error(error ?? 'encountered an error in upsertDetectionIssue');
        return undefined;
      });
  }

  // finds all hitter-sessions from the user's team during the specified period
  async getUsageSummaries(
    filters: IHitterUsageSummaryFilters
  ): Promise<Record<string, IHitterUsageSummary[]>> {
    return await this.post(
      {
        uri: `${HittersRoutes.GetUsageSummaries}`,
      },
      filters
    )
      .then((result: IServerResponse) => {
        if (!result.success || !result.data || result.error) {
          console.warn(
            result.error ?? 'encountered an error in getUsageSummaries'
          );
          return {};
        }

        return result.data;
      })
      .catch((error) => {
        console.error(error ?? 'encountered an error in getUsageSummaries');
        return {};
      });
  }

  // this will always be for the current machine
  async getLatestSessionByHitterID(payload: {
    newerThan: string; // ISO date string
    hitterID: Nullable<string>;
  }): Promise<ISHSummary | undefined> {
    return await this.post(
      {
        uri: HittersRoutes.GetLatestSessionByHitterID,
      },
      payload
    )
      .then((result: IServerResponse) => {
        if (!result.success || !result.data || result.error) {
          console.warn(
            result.error ?? 'encountered an error in getLatestSessionByHitterID'
          );
          return undefined;
        }

        return result.data as ISHSummary;
      })
      .catch((error) => {
        console.error(
          error ?? 'encountered an error in getLatestSessionByHitterID'
        );
        return undefined;
      });
  }
}
